/**
 * SKY GRADIENT — the vertical sky background ramp.
 */

import {
  SKY_KEYFRAMES,
  sampleKeyframes,
  rgb,
  lerpRgb,
  clamp01,
  normalizeTimeOfDay,
} from "./sky-utils.js";

/**
 * Sky colour at a vertical fraction (0 = top of the band, 1 = horizon/sea line)
 * for a given time of day — mirrors the exact stops drawn by drawSky (top → mid
 * at 34% → bottom at 52%, then held). Lets other layers (e.g. the moon's dark
 * side) sample the sky behind them.
 * @returns {number[]} [r, g, b]
 */
export function skyColorAt(timeOfDay, yFrac) {
  var t = normalizeTimeOfDay(timeOfDay);
  var f = clamp01(yFrac);
  var top = sampleKeyframes(SKY_KEYFRAMES, t, "top");
  var mid = sampleKeyframes(SKY_KEYFRAMES, t, "mid");
  var bottom = sampleKeyframes(SKY_KEYFRAMES, t, "bottom");
  if (f <= 0.34) return lerpRgb(top, mid, f / 0.34);
  if (f <= 0.52) return lerpRgb(mid, bottom, (f - 0.34) / 0.18);
  return bottom.slice();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} timeOfDay
 */
export function drawSky(ctx, bounds, timeOfDay) {
  if (!ctx || !bounds) return;
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;

  var t = normalizeTimeOfDay(timeOfDay);
  var top = sampleKeyframes(SKY_KEYFRAMES, t, "top");
  var mid = sampleKeyframes(SKY_KEYFRAMES, t, "mid");
  var bottom = sampleKeyframes(SKY_KEYFRAMES, t, "bottom");

  // Stop positions mirror day.html/night.html: top → mid at 34% → horizon at
  // 52%, then the horizon colour holds down to the sea line.
  var grad = ctx.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, rgb(top));
  grad.addColorStop(0.34, rgb(mid));
  grad.addColorStop(0.52, rgb(bottom));
  grad.addColorStop(1, rgb(bottom));
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);
}
